import {debounce} from 'throttle-debounce';
import {POST} from '../modules/fetch.ts';
import type {Store} from './store.ts';

type DiffFilterState = {
  diffToggle: string;
  filterOverrides: Record<string, boolean>;
  layerOverrides: Record<string, boolean>;
  showUnchangedSheets: boolean;
};

let pendingSave: DiffFilterState | null = null;
let lastIssueLink: string | null = null;

// Each call to saveDiffFilterState() overwrites pendingSave with the latest
// snapshot before re-triggering this debounce.  So when the timer fires, it
// always sends the most recent state.  If a POST is already in-flight and the
// user makes more changes, a new debounce timer starts and will fire with the
// updated pendingSave.  The guard below only skips when saveDiffFilterStateImmediate
// has already sent the data and cleared pendingSave.
const debouncedSave = debounce(1000, async (issueLink: string) => {
  if (!pendingSave) return;

  const data = {...pendingSave};
  pendingSave = null;

  try {
    await POST(`${issueLink}/diff-filter-state`, {data});
  } catch (err) {
    console.error('Failed to save diff filter state:', err);
  }
});

export function saveDiffFilterState(store: Store): void {
  const issueLink = store.issueLink;
  if (!issueLink) return;

  lastIssueLink = issueLink;
  pendingSave = {
    diffToggle: store.diffFilterConfig.diffToggle.value,
    filterOverrides: store.diffFilterConfig.filterOverrides.value,
    layerOverrides: store.diffFilterConfig.layerOverrides.value,
    showUnchangedSheets: store.diffFilterConfig.showUnchangedSheets.value,
  };

  debouncedSave(issueLink);
}

/**
 * Save diff filter state immediately (bypassing debounce).
 * Returns a promise that resolves when the save completes.
 * Use this for reset operations where a page reload follows.
 */
export async function saveDiffFilterStateImmediate(store: Store): Promise<void> {
  const issueLink = store.issueLink;
  if (!issueLink) return;

  // Cancel any pending debounced save
  debouncedSave.cancel();
  pendingSave = null;

  const data = {
    diffToggle: store.diffFilterConfig.diffToggle.value,
    filterOverrides: store.diffFilterConfig.filterOverrides.value,
    layerOverrides: store.diffFilterConfig.layerOverrides.value,
    showUnchangedSheets: store.diffFilterConfig.showUnchangedSheets.value,
  };

  await POST(`${issueLink}/diff-filter-state`, {data});
}

/**
 * Flush any pending debounced save immediately using fetch with keepalive.
 * Called on page unload so the save survives navigation away from the page.
 */
function flushPendingSave() {
  if (!pendingSave || !lastIssueLink) return;

  debouncedSave.cancel();

  const data = {...pendingSave};
  pendingSave = null;

  const {csrfToken} = window.config;
  // keepalive allows the request to outlive the page.  The project's
  // fetch wrapper does not support keepalive, so we use the native API.
  // eslint-disable-next-line no-restricted-syntax
  fetch(`${lastIssueLink}/diff-filter-state`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'x-csrf-token': csrfToken,
    },
    body: JSON.stringify(data),
    keepalive: true,
  });
}

window.addEventListener('beforeunload', flushPendingSave);
// visibilitychange fires more reliably on mobile / tab switching.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    flushPendingSave();
  }
});
